import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./Employee.module.css";
import { useAuth } from "../../components/context/Auth";
import HrmFilters from "../../components/updatedComponents/hrm/HrmFillters";
import SearchBar from "../../components/updatedComponents/FormComponents/SearchBar";
import EmployeeFormModal from "./EmployeeFormModal";
import {
  INITIAL_STAFF,
  calcPfEsi,
  nextStaffId,
  formatDate,
  AllDesignation,
} from "./HrmData";
import DropDown from "../../components/updatedComponents/FormComponents/DropDown";

const EyeIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const SortAscIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 5v14" />
    <path d="M5 11l6-6 6 6" />
  </svg>
);

const SortDescIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 19V5" />
    <path d="M5 13l6 6 6-6" />
  </svg>
);

const Employee = () => {
  const { mode } = useAuth();
  const [staffList, setStaffList] = useState(INITIAL_STAFF);
  const [role, setRole] = useState([]);
  useEffect(() => {
    if (AllDesignation) {
      setRole(
        AllDesignation.map((r) => ({
          label: r.name,
          value: r.name,
        })),
      );
    }
  }, []);
  const [keyword, setKeyword] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("Employee");
  const [sortOrder, setSortOrder] = useState("asc");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null); // null = create mode
  const [viewingStaff, setViewingStaff] = useState(null);

  const tableWrapRef = useRef(null);
  const menuRef = useRef(null);

  // ── Close the action dropdown on outside click or when the table scrolls ──
  useEffect(() => {
    if (openMenuId === null) return undefined;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    const handleScroll = () => setOpenMenuId(null);

    document.addEventListener("mousedown", handleClickOutside);
    const wrap = tableWrapRef.current;
    wrap?.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      wrap?.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [openMenuId]);

  // ── Status cards ─────────────────────────────────────────────────────
  const statusCards = useMemo(() => {
    const total = staffList.length;
    const active = staffList.filter((s) => s.status === "Active").length;
    const pfCount = staffList.filter((s) => s.pfApplicable).length;
    const esiCount = staffList.filter((s) => s.esiApplicable).length;
    const grossTotal = staffList.reduce(
      (sum, s) => sum + (Number(s.monthlySalary) || 0),
      0,
    );

    return [
      { status: "Employee", count: total, subtitle: "All records" },
      { status: "Active", count: active, subtitle: "Attendance ready" },
      { status: "PF", count: pfCount, subtitle: "Employee level" },
      { status: "ESI", count: esiCount, subtitle: "Employee level" },
      {
        status: "Salary",
        count: `₹${grossTotal.toLocaleString("en-IN")}`,
        subtitle: "Gross total",
      },
    ];
  }, [staffList]);

  const getRowStyle = () => ({ background: mode ? "#1B1D21" : "#FFFFFF" });

  const filteredStaff = useMemo(() => {
    const result = staffList.filter((s) => {
      const search =
        s.name.toLowerCase().includes(keyword.toLowerCase()) ||
        s.department.toLowerCase().includes(keyword.toLowerCase()) ||
        s.staffId.toLowerCase().includes(keyword.toLowerCase());

      let matchesFilter = true;
      if (selectedFilter === "Active") matchesFilter = s.status === "Active";
      else if (selectedFilter === "PF") matchesFilter = s.pfApplicable;
      else if (selectedFilter === "ESI") matchesFilter = s.esiApplicable;
      // "Staff" (All) and "Salary" (display-only card) show everyone.

      return search && matchesFilter;
    });

    result.sort((a, b) => {
      const diff = a.name.localeCompare(b.name);
      return sortOrder === "asc" ? diff : -diff;
    });

    return result;
  }, [staffList, keyword, selectedFilter, sortOrder]);

  // ── CRUD ─────────────────────────────────────────────────────────────
  const openAddStaff = () => {
    setEditingStaff(null);
    setIsFormOpen(true);
  };

  const openEditStaff = (staff) => {
    setEditingStaff(staff);
    setIsFormOpen(true);
    setOpenMenuId(null);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingStaff(null);
  };

  const handleSaveStaff = (formData) => {
    if (editingStaff) {
      setStaffList((prev) =>
        prev.map((s) => (s.id === editingStaff.id ? { ...s, ...formData } : s)),
      );
    } else {
      setStaffList((prev) => [
        ...prev,
        { id: Date.now(), staffId: nextStaffId(prev), ...formData },
      ]);
    }
    closeForm();
  };

  const handleDelete = (id) => {
    setStaffList((prev) => prev.filter((s) => s.id !== id));
    setOpenMenuId(null);
  };

  const handleView = (staff) => {
    setViewingStaff(staff);
    setOpenMenuId(null);
  };

  // Ignores clicks on the action-menu column, since that has its own click behavior.
  const handleRowClick = (e, staff) => {
    if (e.target.closest(`.${styles.actionCell}`)) return;
    handleView(staff);
  };

  // ── Toggle the dropdown, computing a fixed on-screen position so it
  //    always renders on top of the table (via portal) and flips upward
  //    when there isn't enough room below (fixes last-row clipping). ─────
  const DROPDOWN_HEIGHT = 140;
  const DROPDOWN_WIDTH = 140;

  const toggleMenu = (e, staffId) => {
    e.stopPropagation();

    if (openMenuId === staffId) {
      setOpenMenuId(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < DROPDOWN_HEIGHT;

    let left = rect.right - DROPDOWN_WIDTH;
    left = Math.max(8, Math.min(left, window.innerWidth - DROPDOWN_WIDTH - 8));

    const top = openUpward ? rect.top - DROPDOWN_HEIGHT : rect.bottom + 6;

    setMenuPos({ top, left });
    setOpenMenuId(staffId);
  };

  const gridColumns = "0.5fr 0.7fr 1.4fr 1.2fr 1fr 1fr 0.6fr 0.6fr 0.9fr 0.6fr";

  const activeMenuStaff =
    openMenuId !== null ? filteredStaff.find((s) => s.id === openMenuId) : null;

  return (
    <div
      className={styles.staff}
      style={mode ? { background: "#2C2E33", border: "none" } : {}}
    >
      <div className={styles.staff_body}>
        <div
          className={styles.staff_filters_wrapper}
          style={mode ? { background: "#232529" } : {}}
        >
          <HrmFilters
            mode={mode}
            headerData={statusCards}
            selectedFilter={selectedFilter}
            handleClick={setSelectedFilter}
            type="staff"
          />
        </div>
      </div>

      <div className={styles.staff_data}>
        <div className={styles.staff_data_filters}>
          <SearchBar
            placeholder="Search Employee..."
            value={keyword}
            padding="5px 10px"
            onChange={(e) => setKeyword(e.target.value)}
            onClear={() => setKeyword("")}
          />

          <div>
            <button
              type="button"
              className={styles.sortOrderBtn}
              onClick={() =>
                setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
              }
              title={
                sortOrder === "asc"
                  ? "Sorted A to Z — click for Z to A"
                  : "Sorted Z to A — click for A to Z"
              }
            >
              {sortOrder === "asc" ? <SortAscIcon /> : <SortDescIcon />}
              <span>{sortOrder === "asc" ? "Ascending" : "Descending"}</span>
            </button>

            <button
              type="button"
              className={styles.addShiftBtn}
              onClick={openAddStaff}
            >
              + Add Employee
            </button>

            <DropDown
              label={"Designation"}
              listLabel={"All"}
              options={role}
              onSelect={(option) => setRole(option.value)}
              onReject={() => setRole(null)}
            />
          </div>
        </div>

        <div className={styles.staffTableWrap} ref={tableWrapRef}>
          <ul className={styles.staffTable}>
            <li
              className={styles.staffTableHeader}
              style={{
                gridTemplateColumns: gridColumns,
                background: mode ? "#232529" : "#d9dce5",
                color: mode ? "#fff" : "#646b88",
              }}
            >
              <div>S.No.</div>
              <div>Employee ID</div>
              <div>Name</div>
              <div>Designation</div>
              <div>Joining</div>
              <div>Salary</div>
              <div>PF</div>
              <div>ESI</div>
              <div>Status</div>
              <div className={styles.actionHeaderCell}>Action</div>
            </li>

            {filteredStaff.length === 0 && (
              <li className={styles.staffEmptyRow}>
                No Employee match this search.
              </li>
            )}

            {filteredStaff.map((staff, index) => (
              <li
                key={staff.id}
                className={`${styles.staffTableRow} ${mode ? styles.staffTableRowDark : ""}`}
                style={{ gridTemplateColumns: gridColumns, ...getRowStyle() }}
                onClick={(e) => handleRowClick(e, staff)}
                title="Click to view staff details"
              >
                <div>{index + 1}</div>
                <div>{staff.staffId}</div>
                <div className={styles.nameCell}>{staff.name}</div>
                <div>{staff.department}</div>
                <div>{formatDate(staff.joiningDate)}</div>
                <div>
                  &#8377;{Number(staff.monthlySalary).toLocaleString("en-IN")}
                </div>
                <div>{staff.pfApplicable ? "Yes" : "No"}</div>
                <div>{staff.esiApplicable ? "Yes" : "No"}</div>
                <div>
                  <span
                    className={
                      staff.status === "Active"
                        ? styles.statusActive
                        : styles.statusInactive
                    }
                  >
                    {staff.status}
                  </span>
                </div>

                <div className={styles.actionCell}>
                  <button
                    type="button"
                    className={styles.actionDotsBtn}
                    onClick={(e) => toggleMenu(e, staff.id)}
                    aria-label="Row actions"
                  >
                    &#8942;
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Portal-rendered dropdown ─────────────────────────────────────
          Escapes the scrollable <ul> entirely (mounted on document.body),
          positioned with `position: fixed` coordinates computed in
          toggleMenu, and flips upward automatically near the bottom of
          the viewport so it's never hidden for the last rows. ─────────── */}
      {activeMenuStaff &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className={`${styles.actionDropdown} ${mode ? styles.actionDropdownDark : ""}`}
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              style={{ color: mode ? "#ffffff" : "#000000" }}
              className={styles.actionDropdownItem}
              onClick={() => handleView(activeMenuStaff)}
            >
              <span
                style={{ color: mode ? "#ffffff" : "#000000" }}
                className={styles.actionDropdownIcon}
              >
                <EyeIcon />
              </span>
              <span style={{ color: mode ? "#ffffff" : "#000000" }}>View</span>
            </button>
            <button
              type="button"
              style={{ color: mode ? "#ffffff" : "#000000" }}
              className={styles.actionDropdownItem}
              onClick={() => openEditStaff(activeMenuStaff)}
            >
              <span
                style={{ color: mode ? "#ffffff" : "#000000" }}
                className={styles.actionDropdownIcon}
              >
                <EditIcon />
              </span>
              <span style={{ color: mode ? "#ffffff" : "#000000" }}>Edit</span>
            </button>
            <button
              style={{ color: mode ? "#ffffff" : "#000000" }}
              type="button"
              className={`${styles.actionDropdownItem} ${styles.actionDropdownItemDanger}`}
              onClick={() => handleDelete(activeMenuStaff.id)}
            >
              <span
                style={{ color: mode ? "#ffffff" : "#000000" }}
                className={styles.actionDropdownIcon}
              >
                <TrashIcon />
              </span>
              <span style={{ color: mode ? "#ffffff" : "#000000" }}>
                Delete
              </span>
            </button>
          </div>,
          document.body,
        )}

      {/* ── Add / Edit modal ─────────────────────────────────────────────── */}
      {isFormOpen && (
        <EmployeeFormModal
          mode={mode}
          initialStaff={editingStaff}
          onClose={closeForm}
          onSave={handleSaveStaff}
        />
      )}

      {/* ── Read-only View modal ─────────────────────────────────────────── */}
      {viewingStaff && (
        <div
          className={styles.modalOverlay}
          onClick={() => setViewingStaff(null)}
        >
          <div
            className={`${styles.viewModalCard} ${mode ? styles.viewModalCardDark : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.modalCloseBtn}
              onClick={() => setViewingStaff(null)}
            >
              &times;
            </button>

            <p className={styles.modalEyebrow}>EMPLOYEE</p>
            <h2 className={styles.modalTitle}>{viewingStaff.name}</h2>

            <div className={styles.viewGrid}>
              <div>
                <span>Employee ID</span>
                <strong>{viewingStaff.staffId}</strong>
              </div>
              <div>
                <span>Mobile</span>
                <strong>{viewingStaff.mobile || "—"}</strong>
              </div>
              <div>
                <span>Department</span>
                <strong>{viewingStaff.department}</strong>
              </div>
              <div>
                <span>Designation</span>
                <strong>{viewingStaff.designation || "—"}</strong>
              </div>
              <div>
                <span>Joining Date</span>
                <strong>{formatDate(viewingStaff.joiningDate)}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{viewingStaff.status}</strong>
              </div>
              <div>
                <span>CTC</span>
                <strong>
                  &#8377;{Number(viewingStaff.ctc || 0).toLocaleString("en-IN")}
                </strong>
              </div>
              <div>
                <span>Monthly Gross</span>
                <strong>
                  &#8377;
                  {Number(viewingStaff.monthlyGross || 0).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>
              <div>
                <span>Basic Salary</span>
                <strong>
                  &#8377;
                  {Number(viewingStaff.basicSalary || 0).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>
              <div>
                <span>Monthly Salary</span>
                <strong>
                  &#8377;
                  {Number(viewingStaff.monthlySalary || 0).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>
            </div>

            <div className={styles.viewPfEsiRow}>
              {(() => {
                const { pfEmployee, pfEmployer, esiEmployee, esiEmployer } =
                  calcPfEsi(viewingStaff);
                return (
                  <>
                    <div>
                      <span>PF Employee</span>
                      <strong>
                        {viewingStaff.pfApplicable
                          ? `₹${pfEmployee.toLocaleString("en-IN")}`
                          : "N/A"}
                      </strong>
                    </div>
                    <div>
                      <span>PF Employer</span>
                      <strong>
                        {viewingStaff.pfApplicable
                          ? `₹${pfEmployer.toLocaleString("en-IN")}`
                          : "N/A"}
                      </strong>
                    </div>
                    <div>
                      <span>ESI Employee</span>
                      <strong>
                        {viewingStaff.esiApplicable
                          ? `₹${esiEmployee.toLocaleString("en-IN")}`
                          : "N/A"}
                      </strong>
                    </div>
                    <div>
                      <span>ESI Employer</span>
                      <strong>
                        {viewingStaff.esiApplicable
                          ? `₹${esiEmployer.toLocaleString("en-IN")}`
                          : "N/A"}
                      </strong>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setViewingStaff(null)}
              >
                Close
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={() => {
                  const staff = viewingStaff;
                  setViewingStaff(null);
                  openEditStaff(staff);
                }}
              >
                Edit Employee
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee;
